import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { FaTimes } from "react-icons/fa";

const DEFAULT_NOTIFICATION_PREFS = {
  sessionRemindersNotification: true,
  newSessionNotification: true,
  cancelAndRescheduleNotification: true,
};

const normalizePrefs = (prefs) => {
  const raw =
    typeof prefs === "string"
      ? (() => {
          try {
            return JSON.parse(prefs);
          } catch {
            return {};
          }
        })()
      : prefs || {};

  return {
    ...DEFAULT_NOTIFICATION_PREFS,
    ...raw,
  };
};

const validatePassword = (password) =>
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
    password,
  );

const SettingToggle = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    className={`pp-toggle pp-settings-toggle ${checked ? "on" : ""}`}
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
  >
    <span className="pp-toggle-thumb" />
  </button>
);

const ProviderSettingsTab = ({
  provider,
  getProviderId,
  onProviderUpdated,
  isAdminView = false,
  onAdminChangeEmail,
}) => {
  const [savingKey, setSavingKey] = useState(null);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showVerificationSent, setShowVerificationSent] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);
  const [changePasswordCurrent, setChangePasswordCurrent] = useState("");
  const [changePasswordNew, setChangePasswordNew] = useState("");
  const [changePasswordConfirm, setChangePasswordConfirm] = useState("");
  const [pendingNewEmail, setPendingNewEmail] = useState("");

  const providerType = provider?.providerType || "Therapist";
  const isTherapist =
    !provider?.providerType || provider.providerType === "Therapist";
  const isCareManager = providerType === "Care Manager";
  const showReferrals = isTherapist;
  const showNotifications = isTherapist || isCareManager;
  const accountEmail = provider?.email || "";

  const prefs = useMemo(
    () => normalizePrefs(provider?.notification_preferences),
    [provider?.notification_preferences],
  );

  const acceptingNewReferrals = provider?.acceptingNewReferrals !== false;

  const persistProvider = async (payload) => {
    const request = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${getProviderId()}`,
      {
        email: provider.email,
        firstname: provider.firstname,
        lastname: provider.lastname,
        providerType: provider.providerType,
        ...payload,
      },
    );
    return request;
  };

  const updateReferrals = async () => {
    const next = !acceptingNewReferrals;
    setSavingKey("referrals");
    try {
      const request = await persistProvider({ acceptingNewReferrals: next });
      if (request.data.success) {
        const saved =
          request.data.data?.acceptingNewReferrals !== undefined
            ? request.data.data.acceptingNewReferrals !== false
            : next;
        onProviderUpdated({
          ...provider,
          ...request.data.data,
          acceptingNewReferrals: saved,
        });
        toast.success(
          saved
            ? "Accepting new referrals turned on."
            : "Accepting new referrals turned off. Provider hidden from new referral and directory search results. Existing sessions and patients are unchanged.",
        );
      } else {
        toast.error(request.data.message || "Failed to update referral setting.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update referral setting.",
      );
    } finally {
      setSavingKey(null);
    }
  };

  const NOTIFICATION_LABELS = {
    sessionRemindersNotification: "Session Reminders",
    newSessionNotification: "New Session Scheduled",
    cancelAndRescheduleNotification: "Cancellations and Reschedules",
  };

  const updateNotification = async (key) => {
    const nextValue = !prefs[key];
    const nextPrefs = {
      ...prefs,
      [key]: nextValue,
    };
    setSavingKey(key);
    try {
      const request = await persistProvider({
        notification_preferences: nextPrefs,
      });
      if (request.data.success) {
        const savedPrefs =
          request.data.data?.notification_preferences || nextPrefs;
        onProviderUpdated({
          ...provider,
          ...request.data.data,
          notification_preferences: normalizePrefs(savedPrefs),
        });
        toast.success(
          `${NOTIFICATION_LABELS[key] || "Notification"} turned ${
            nextValue ? "on" : "off"
          }.`,
        );
      } else {
        toast.error(
          request.data.message || "Failed to update notification preference.",
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to update notification preference.",
      );
    } finally {
      setSavingKey(null);
    }
  };

  const openChangePassword = () => {
    setChangePasswordCurrent("");
    setChangePasswordNew("");
    setChangePasswordConfirm("");
    setShowChangePassword(true);
  };

  const closeChangePassword = () => {
    if (passwordSubmitting) return;
    setShowChangePassword(false);
    setChangePasswordCurrent("");
    setChangePasswordNew("");
    setChangePasswordConfirm("");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!changePasswordCurrent) {
      toast.error("Please enter your current password.");
      return;
    }

    if (!validatePassword(changePasswordNew)) {
      toast.error(
        "New password must be at least 8 characters and include uppercase, lowercase, number, and special character.",
      );
      return;
    }

    if (changePasswordNew !== changePasswordConfirm) {
      toast.error("Passwords do not match.");
      return;
    }

    setPasswordSubmitting(true);
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("user_id");
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/update-password`,
        {
          currentPassword: changePasswordCurrent,
          password: changePasswordNew,
          email: accountEmail,
          providerId: getProviderId(),
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );

      if (response.data.success) {
        toast.success("Password changed successfully.");
        closeChangePassword();
      } else {
        toast.error(response.data.message || "Failed to change password.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to change password.",
      );
    } finally {
      setPasswordSubmitting(false);
    }
  };

  const openChangeEmail = () => {
    setNewEmail("");
    setCurrentPassword("");
    setShowChangeEmail(true);
  };

  const closeChangeEmail = () => {
    if (emailSubmitting) return;
    setShowChangeEmail(false);
    setNewEmail("");
    setCurrentPassword("");
  };

  const handleSendVerificationEmail = async (e) => {
    e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!currentPassword) {
      toast.error("Please enter your current password.");
      return;
    }

    setEmailSubmitting(true);
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("user_id");
      const response = await axios.put(
        `${process.env.REACT_APP_BASE_URL}/specialist/update-email`,
        {
          providerId: getProviderId(),
          email: trimmed,
          currentPassword,
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );

      if (response.data.success) {
        setPendingNewEmail(trimmed);
        setShowChangeEmail(false);
        setNewEmail("");
        setCurrentPassword("");
        setShowVerificationSent(true);
      } else {
        toast.error(
          response.data.message || "Failed to send verification email.",
        );
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Failed to send verification email.",
      );
    } finally {
      setEmailSubmitting(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!accountEmail) {
      toast.error("No email on file for this provider.");
      return;
    }

    setSavingKey("reset-password");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/specialist/forgot-password`,
        { email: accountEmail },
      );

      if (response.data.success) {
        toast.success("Password reset email sent.");
      } else {
        toast.error(response.data.message || "Failed to send reset email.");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to send reset email.",
      );
    } finally {
      setSavingKey(null);
    }
  };

  const busy =
    Boolean(savingKey) || emailSubmitting || passwordSubmitting;

  return (
    <div
      className={`pp-settings-tab${isAdminView ? " pp-settings-tab--admin" : ""}`}
    >
      {showReferrals && (
        <div className="pp-section pp-settings-section">
          <div className="pp-section-header">
            <div>
              <h3>Referrals</h3>
            </div>
          </div>
          <div className="pp-section-body pp-settings-body">
            <div className="pp-settings-row">
              <div className="pp-settings-copy">
                <div className="pp-settings-title">Accepting new referrals</div>
                <p className="pp-settings-desc">
                  When off, this provider will not appear as an option for new
                  patient referrals or directory searches.
                </p>
              </div>
              <SettingToggle
                checked={acceptingNewReferrals}
                disabled={busy}
                label="Accepting new referrals"
                onChange={updateReferrals}
              />
            </div>
          </div>
        </div>
      )}

      {showNotifications && (
        <div className="pp-section pp-settings-section">
          <div className="pp-section-header">
            <div>
              <h3>Notifications</h3>
            </div>
          </div>
          <div className="pp-section-body pp-settings-body">
            <div className="pp-settings-row">
              <div className="pp-settings-copy">
                <div className="pp-settings-title">Session Reminders</div>
                <p className="pp-settings-desc">
                  15 minutes before session start.
                </p>
              </div>
              <SettingToggle
                checked={Boolean(prefs.sessionRemindersNotification)}
                disabled={busy}
                label="Session Reminders"
                onChange={() =>
                  updateNotification("sessionRemindersNotification")
                }
              />
            </div>

            <div className="pp-settings-row">
              <div className="pp-settings-copy">
                <div className="pp-settings-title">New Session Scheduled</div>
                <p className="pp-settings-desc">
                  Client scheduled an appointment.
                </p>
              </div>
              <SettingToggle
                checked={Boolean(prefs.newSessionNotification)}
                disabled={busy}
                label="New Session Scheduled"
                onChange={() => updateNotification("newSessionNotification")}
              />
            </div>

            <div className="pp-settings-row">
              <div className="pp-settings-copy">
                <div className="pp-settings-title">
                  Cancellations and Reschedules
                </div>
                <p className="pp-settings-desc">
                  When a client cancels or reschedules.
                </p>
              </div>
              <SettingToggle
                checked={Boolean(prefs.cancelAndRescheduleNotification)}
                disabled={busy}
                label="Cancellations and Reschedules"
                onChange={() =>
                  updateNotification("cancelAndRescheduleNotification")
                }
              />
            </div>
          </div>
        </div>
      )}

      <div className="pp-section pp-settings-section">
        <div className="pp-section-header">
          <div>
            <h3>Account</h3>
          </div>
        </div>
        <div className="pp-section-body pp-settings-body">
          <div className="pp-settings-row">
            <div className="pp-settings-copy">
              <div className="pp-settings-title">Account email</div>
              <p className="pp-settings-desc">
                {accountEmail || "No email on file."}
              </p>
            </div>
            <button
              type="button"
              className="pp-settings-reset-btn"
              onClick={
                isAdminView
                  ? () => {
                      if (typeof onAdminChangeEmail === "function") {
                        onAdminChangeEmail();
                        return;
                      }
                      toast.info(
                        "To update this provider's email, use the Profile tab.",
                      );
                    }
                  : openChangeEmail
              }
              disabled={busy || !accountEmail}
            >
              Change Email
            </button>
          </div>

          {isAdminView ? (
            <div className="pp-settings-row">
              <div className="pp-settings-copy">
                <div className="pp-settings-title">Reset password</div>
                <p className="pp-settings-desc">
                  Send a password reset email.
                </p>
              </div>
              <button
                type="button"
                className="pp-settings-reset-btn"
                onClick={handleSendResetEmail}
                disabled={busy || !accountEmail}
              >
                {savingKey === "reset-password"
                  ? "Sending..."
                  : "Send Reset Email"}
              </button>
            </div>
          ) : (
            <div className="pp-settings-row">
              <div className="pp-settings-copy">
                <div className="pp-settings-title">Change Password</div>
                <p className="pp-settings-desc">Update password.</p>
              </div>
              <button
                type="button"
                className="pp-settings-reset-btn"
                onClick={openChangePassword}
                disabled={busy || !accountEmail}
              >
                Change Password
              </button>
            </div>
          )}
        </div>
      </div>

      {!isAdminView && showChangeEmail &&
        createPortal(
          <div
            className="pp-settings-modal-overlay"
            onClick={closeChangeEmail}
          >
            <div
              className="pp-settings-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pp-change-email-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="pp-settings-modal-close"
                onClick={closeChangeEmail}
                aria-label="Close"
                disabled={emailSubmitting}
              >
                <FaTimes size={14} />
              </button>
              <h2 id="pp-change-email-title" className="pp-settings-modal-title">
                Change Email
              </h2>
              <p className="pp-settings-modal-intro">
                Enter your new email address and current password. A verification
                link will be sent to confirm the change.
              </p>
              <form onSubmit={handleSendVerificationEmail}>
                <label className="pp-settings-modal-label" htmlFor="pp-new-email">
                  New Email Address
                </label>
                <input
                  id="pp-new-email"
                  type="email"
                  className="pp-settings-modal-input"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email address"
                  autoComplete="email"
                  disabled={emailSubmitting}
                  required
                />
                <label
                  className="pp-settings-modal-label"
                  htmlFor="pp-current-password"
                >
                  Current Password
                </label>
                <input
                  id="pp-current-password"
                  type="password"
                  className="pp-settings-modal-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  autoComplete="current-password"
                  disabled={emailSubmitting}
                  required
                />
                <button
                  type="submit"
                  className="pp-settings-modal-primary"
                  disabled={emailSubmitting}
                >
                  {emailSubmitting
                    ? "Sending..."
                    : "Send Verification Email"}
                </button>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {!isAdminView && showChangePassword &&
        createPortal(
          <div
            className="pp-change-password-drawer-overlay"
            onClick={closeChangePassword}
          >
            <aside
              className="pp-change-password-drawer"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pp-change-password-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pp-change-password-drawer-header">
                <div className="pp-change-password-drawer-heading">
                  <h2 id="pp-change-password-title">Change Password</h2>
                  <p>
                    Choose a strong password you haven&apos;t used before.
                  </p>
                </div>
                <button
                  type="button"
                  className="pp-change-password-drawer-close"
                  onClick={closeChangePassword}
                  aria-label="Close"
                  disabled={passwordSubmitting}
                >
                  <FaTimes size={14} />
                </button>
              </div>

              <form
                className="pp-change-password-drawer-form"
                onSubmit={handleChangePassword}
              >
                <div className="pp-change-password-drawer-body">
                  <div className="pp-change-password-field">
                    <label
                      className="pp-change-password-label"
                      htmlFor="pp-change-password-current"
                    >
                      Current Password
                    </label>
                    <input
                      id="pp-change-password-current"
                      type="password"
                      className="pp-change-password-input"
                      value={changePasswordCurrent}
                      onChange={(e) =>
                        setChangePasswordCurrent(e.target.value)
                      }
                      autoComplete="current-password"
                      disabled={passwordSubmitting}
                      required
                    />
                  </div>

                  <div className="pp-change-password-field">
                    <label
                      className="pp-change-password-label"
                      htmlFor="pp-change-password-new"
                    >
                      New Password
                    </label>
                    <input
                      id="pp-change-password-new"
                      type="password"
                      className="pp-change-password-input"
                      value={changePasswordNew}
                      onChange={(e) => setChangePasswordNew(e.target.value)}
                      autoComplete="new-password"
                      disabled={passwordSubmitting}
                      required
                    />
                  </div>

                  <div className="pp-change-password-field">
                    <label
                      className="pp-change-password-label"
                      htmlFor="pp-change-password-confirm"
                    >
                      Confirm Password
                    </label>
                    <input
                      id="pp-change-password-confirm"
                      type="password"
                      className="pp-change-password-input"
                      value={changePasswordConfirm}
                      onChange={(e) =>
                        setChangePasswordConfirm(e.target.value)
                      }
                      autoComplete="new-password"
                      disabled={passwordSubmitting}
                      required
                    />
                  </div>

                  <ul className="pp-password-requirements">
                    <li>Minimum 8 Characters</li>
                    <li>1 Uppercase</li>
                    <li>1 Lowercase</li>
                    <li>1 Number</li>
                    <li>1 Special Character</li>
                  </ul>
                </div>

                <div className="pp-change-password-drawer-footer">
                  <button
                    type="submit"
                    className="pp-change-password-submit"
                    disabled={passwordSubmitting}
                  >
                    {passwordSubmitting ? "Updating..." : "Update Password"}
                  </button>
                </div>
              </form>
            </aside>
          </div>,
          document.body,
        )}

      {showVerificationSent &&
        createPortal(
          <div
            className="pp-settings-modal-overlay"
            onClick={() => setShowVerificationSent(false)}
          >
            <div
              className="pp-settings-modal pp-settings-modal-success"
              role="dialog"
              aria-modal="true"
              aria-labelledby="pp-verify-sent-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pp-settings-modal-icon" aria-hidden="true">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="20"
                    cy="20"
                    r="18"
                    stroke="#33b697"
                    strokeWidth="2"
                  />
                  <path
                    d="M11 15.5h18v11a2 2 0 0 1-2 2H13a2 2 0 0 1-2-2v-11z"
                    stroke="#33b697"
                    strokeWidth="1.8"
                    fill="none"
                  />
                  <path
                    d="M11 15.5 20 22l9-6.5"
                    stroke="#33b697"
                    strokeWidth="1.8"
                    fill="none"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 id="pp-verify-sent-title" className="pp-settings-modal-title">
                Verification Email Sent
              </h2>
              <p className="pp-settings-modal-intro">
                We&apos;ve sent a verification link to{" "}
                <strong>{pendingNewEmail}</strong>. Please check your inbox and
                click the link to confirm your new email address.
              </p>
              <div className="pp-settings-modal-alert" role="status">
                The link will expire in 24 hours.
              </div>
              <button
                type="button"
                className="pp-settings-modal-primary"
                onClick={() => setShowVerificationSent(false)}
              >
                Done
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ProviderSettingsTab;
